import fetchWrapper from "../utils/fetchWrapper";
import { ConfirmEmailSchemaType } from "../utils/schema/confirmEmail-validator-schema";
import { LoginSchemaType } from "../utils/schema/login-validator-schema";
import { NewpasswordSchemaType } from "../utils/schema/newpassword-validator-schema";
import { UserFormDataTypes } from "../utils/schema/signup-validator-schema";

const baseurl = process.env.GATSBY_BASE_URL;

export const signUp = async <T = sigUpType>({
  addressLine1,
  city,
  password,
  email,
  fname,
  lname,
  state,
  zipCode,
}: UserFormDataTypes): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(`${baseurl}/sign-up?action=signup`, {
      method: "POST",
      body: JSON.stringify({
        addressLine1,
        city,
        password,
        email,
        fname,
        lname,
        state,
        zipCode,
      }),
    });

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to sign up. Please try again later.");
    }
  }
};

export const notConfirmUser = async <T>({
  email,
}: {
  email: string;
}): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(
      `${baseurl}/sign-up?action=again-confirm-user`,
      {
        method: "POST",
        body: JSON.stringify({
          email,
        }),
      }
    );

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to sign up. Please try again later.");
    }
  }
};

export const logOut = async ({
  global = false,
  RefreshToken,
  token,
}: {
  global?: boolean;
  RefreshToken: string;
  token: string;
}) => {
  try {
    const res = await fetchWrapper(`${baseurl}/login?action=logout`, {
      headers: {
        Authorization: "Bearer " + (global ? token : RefreshToken),
      },
      body: JSON.stringify({
        ss: "ss",
        ...(global ? { global: "true" } : {}),
      }),
      method: "POST",
    });
    return res;
  } catch (err: any) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to logout. Please try again later.");
    }
  }
};

export const confirmSignUp = async <T = confirmSignUpType>({
  confirmationCode,
  email,
  password,
}: ConfirmEmailSchemaType & {
  email: string;
  password: string;
}): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(
      `${baseurl}/sign-up?action=confirm-signup`,
      {
        method: "POST",
        body: JSON.stringify({
          confirmCode: confirmationCode,
          email,
          password,
        }),
      }
    );

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to confirmSignUp. Please try again later.");
    }
  }
};

export const signIn = async <T = signInType>({
  email,
  password,
}: LoginSchemaType): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(`${baseurl}/login?action=signin`, {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to signIn. Please try again later.");
    }
  }
};

export const confirmSignIn = async <T = signInType>({
  password,
  email,
  session,
}: NewpasswordSchemaType & { session: string; email: string }): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(
      `${baseurl}/login?action=NEW_PASSWORD_REQUIRED`,
      {
        method: "POST",
        body: JSON.stringify({ password, email }),
        headers: {
          session: session,
        },
      }
    );

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to confirmSignIn. Please try again later.");
    }
  }
};

export const resetPassword = async <T>({
  email,
}: {
  email: string;
}): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(
      `${baseurl}/forget-password?action=forgot-password`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      }
    );

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to resetPassword. Please try again later.");
    }
  }
};

export const confirmResetPassword = async <T>({
  password,
  email,
  code,
}: NewpasswordSchemaType & { code: string; email: string }): Promise<T> => {
  try {
    const res = await fetchWrapper<T>(
      `${baseurl}/forget-password?action=confirm-forgot-password`,
      {
        method: "POST",
        body: JSON.stringify({ password, email, code }),
      }
    );

    return res;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error(
        "Failed to confirmResetPassword. Please try again later."
      );
    }
  }
};

export const getUserData = async <T>(): Promise<T> => {
  const token = localStorage.getItem("AccessToken");

  if (!token) {
    throw new Error("No access token found");
  }

  try {
    const res = await fetchWrapper<T>(`${baseurl}/dashboard`, {
      headers: {
        Authorization: "Bearer " + token,
      },
      isAuth: true,
    });
    const data = extractNameAndEmail(res);
    return data;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error("Failed to getUserData. Please try again later.");
    }
  }
};

export const tokenSetter = ({
  RefreshToken,
  AccessToken,
  ExpiresIn,
  IdToken,
}: AuthTokens) => {
  const now = new Date().getTime();
  const expireIn = now + Number(ExpiresIn) * 1000;
  localStorage.setItem("RefreshToken", RefreshToken);
  localStorage.setItem("AccessToken", AccessToken);
  localStorage.setItem("ExpiresIn", expireIn.toString());
  localStorage.setItem("IdToken", IdToken);
};
export const tokenRemover = () => {
  localStorage.removeItem("RefreshToken");
  localStorage.removeItem("AccessToken");
  localStorage.removeItem("ExpiresIn");
  localStorage.removeItem("IdToken");
};

function extractNameAndEmail(jsonData: any): any {
  const userAttributes = jsonData.data.UserAttributes;

  let name = "";
  let email = "";

  userAttributes.forEach((attribute: attribute) => {
    if (attribute.Name === "name") {
      name = attribute.Value;
    } else if (attribute.Name === "email") {
      email = attribute.Value;
    }
  });

  return {
    name: name,
    email: email,
  };
}

export const fetchAuthTokens = async () => {
  const RefreshToken = localStorage.getItem("RefreshToken");
  if (!RefreshToken) throw new Error();
  try {
    const res = await fetchWrapper<SignInTypeBase>(
      `${baseurl}/login?action=token`,
      {
        method: "POST",
        body: JSON.stringify({ d: "sss" }),
        headers: {
          Authorization: "Bearer " + RefreshToken,
        },
      }
    );
    const tokens = {
      RefreshToken: RefreshToken,
      AccessToken: res.data.AuthenticationResult.AccessToken,
      IdToken: res.data.AuthenticationResult.IdToken,
      ExpiresIn: res.data.AuthenticationResult.ExpiresIn,
    };
    tokenSetter({ ...tokens, TokenType: "Bearer" });
    return tokens;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      tokenRemover();
      throw new Error("Failed to fetch Tokens.");
    }
  }
};
export const fetchLocalAuthTokens = () => {
  const RefreshToken = localStorage.getItem("RefreshToken");
  const AccessToken = localStorage.getItem("AccessToken");
  const ExpiresIn = localStorage.getItem("ExpiresIn");
  const IdToken = localStorage.getItem("IdToken");

  if (!RefreshToken || !AccessToken || !ExpiresIn || !IdToken) {
    tokenRemover();
    return {};
  }
  return {
    RefreshToken,
    AccessToken,
    ExpiresIn: parseInt(ExpiresIn, 10),
    IdToken,
  };
};

export const isAccessTokenExpired = (expiresIn: number) => {
  if (!expiresIn) return true;
  const expireTime = expiresIn;
  const now = new Date().getTime();
  return now >= expireTime;
};
